/**
 * Универсальный рендерер отчётов.
 *
 * Страницы под каждый отчёт нет и не будет: состав измерений, показателей
 * и фильтров приходит с сервера из описания отчёта. Добавление отчёта
 * в реестр на бэкенде сразу даёт рабочую страницу здесь.
 *
 * Состояние живёт в адресе страницы: ссылкой на конкретный срез можно
 * поделиться, а кнопка «назад» возвращает предыдущий разрез, а не выкидывает
 * из отчёта.
 *
 * Всё, что сервер сообщил о неполноте данных и о сужении прав, показывается
 * явно. Отчёт, по которому нельзя сказать, чего в нём не хватает, опаснее
 * отсутствия отчёта.
 */

import { Icon } from '../components/Icon'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../api'
import type {
  AdminUser,
  CompareMode,
  CurrentUser,
  DimensionValue,
  ReportColumn,
  ReportDescriptor,
  ReportResult,
} from '../types'
import { Badge, Card, CheckChip, Empty, Loading, Notice, SkeletonPanel } from '../components/ui'
import { Segmented } from '../finance/parts'
import { humanForbidden, setScopeNames } from '../finance/data'
import { useDelayedFlag, useMediaQuery } from '../finance/motion'
import { Sheet } from '../components/Sheet'
import { BarRows, ColumnChart } from '../finance/charts'
import { monthLabel, shortMonth } from '../finance/period'
import '../finance/finance.css'
import { DataTable } from '../components/DataTable'
import {
  deltaPercent,
  deltaPoints,
  endOfCurrentMonth,
  formatDate,
  formatDateTime,
  formatMonth,
  formatMoneyShort,
  formatNumber,
  formatPercent,
  monthsAgo,
  pluralize,
} from '../format'

interface Period {
  from: string
  to: string
}

/** Последний день прошлого месяца — конец последнего закончившегося месяца. */
function endOfLastMonth(): string {
  const now = new Date()
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), 0)).toISOString().slice(0, 10)
}

function todayIso(): string {
  const now = new Date()
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())).toISOString().slice(0, 10)
}

/**
 * «3, 6, 12 месяцев» — закончившиеся месяцы: текущий в 1С ещё не закрыт,
 * и сравнение «неполный октябрь + 11 месяцев» с полными 12 показало бы
 * падение, которого нет. Текущий месяц — отдельный пресет, с 1-го по сегодня,
 * и подписан как неполный.
 */
const PERIOD_PRESETS: Array<{ code: string; title: string; period: () => Period }> = [
  { code: 'month', title: 'Текущий месяц', period: () => ({ from: monthsAgo(0), to: todayIso() }) },
  { code: 'quarter', title: '3 месяца', period: () => ({ from: monthsAgo(3), to: endOfLastMonth() }) },
  { code: 'half', title: '6 месяцев', period: () => ({ from: monthsAgo(6), to: endOfLastMonth() }) },
  { code: 'year', title: '12 месяцев', period: () => ({ from: monthsAgo(12), to: endOfLastMonth() }) },
  { code: 'all', title: 'Всё время', period: () => ({ from: monthsAgo(60), to: endOfCurrentMonth() }) },
]

const COMPARE_OPTIONS: Array<{ code: '' | CompareMode; title: string }> = [
  { code: '', title: 'Без сравнения' },
  { code: 'previous_period', title: 'С прошлым периодом' },
  { code: 'previous_year', title: 'С прошлым годом' },
]

export function Report({ user }: { user: CurrentUser }) {
  const { code = '' } = useParams()
  const [params, setParams] = useSearchParams()

  const [descriptor, setDescriptor] = useState<ReportDescriptor | null>(null)
  const [result, setResult] = useState<ReportResult | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, DimensionValue[]>>({})
  const [people, setPeople] = useState<AdminUser[]>([])

  // --- Состояние из адреса -------------------------------------------------
  // Явный период (from/to) сильнее пресета: так приходят ссылки из разделов,
  // и «Открыть в отчёте» показывает тот же месяц, а не 12 месяцев от сегодня.
  const explicitFrom = params.get('from')
  const explicitTo = params.get('to')
  const custom = isIsoDate(explicitFrom) && isIsoDate(explicitTo) && explicitFrom! <= explicitTo!
  // Ссылка с перепутанными или нераспознанными датами: не молча 12 месяцев,
  // а поправленный адрес и строка о том, что именно поправлено.
  const inverted = isIsoDate(explicitFrom) && isIsoDate(explicitTo) && explicitFrom! > explicitTo!
  const unreadable = !custom && !inverted && (explicitFrom !== null || explicitTo !== null)
  const [periodNotice, setPeriodNotice] = useState<string | null>(null)
  const preset = custom ? 'custom' : (params.get('p') ?? 'year')
  const compare = (params.get('c') ?? '') as '' | CompareMode
  const asUser = params.get('as') ?? ''
  const groupBy = useMemo(() => splitParam(params.get('g')), [params])
  const metrics = useMemo(() => splitParam(params.get('m')), [params])

  const filters = useMemo(() => {
    const result: Record<string, string[]> = {}
    for (const [key, value] of params.entries()) {
      if (!key.startsWith('f.')) continue
      const list = splitParam(value)
      if (list.length > 0) result[key.slice(2)] = list
    }
    return result
  }, [params])

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key)
            else next.set(key, value)
          }
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const period = useMemo(
    () =>
      custom
        ? { from: explicitFrom!, to: explicitTo! }
        : (PERIOD_PRESETS.find((item) => item.code === preset) ?? PERIOD_PRESETS[3]!).period(),
    [preset, custom, explicitFrom, explicitTo],
  )

  useEffect(() => {
    if (inverted) {
      // Меняем местами по месяцам: «с марта 2027 по сентябрь 2026» → «с сентября 2026 по март 2027».
      const from = `${explicitTo!.slice(0, 7)}-01`
      const to = monthEndOf(explicitFrom!.slice(0, 7))
      setPeriodNotice(
        `Начало периода (${formatMonth(`${explicitFrom!.slice(0, 7)}-01`)}) было позже конца (${formatMonth(`${explicitTo!.slice(0, 7)}-01`)}) — ` +
          `даты поменяны местами: показан период ${formatDate(from)} — ${formatDate(to)}.`,
      )
      patch({ from, to })
    } else if (unreadable) {
      setPeriodNotice('Период в ссылке не распознан — показаны последние 12 закончившихся месяцев. Выберите свой период заново.')
      patch({ from: null, to: null })
    }
  }, [inverted, unreadable, explicitFrom, explicitTo, patch])

  // За какие даты в отчёте вообще есть строки: подсказка у пресетов
  // и в пустой таблице. Один лёгкий запрос по месяцам при открытии отчёта.
  const [extent, setExtent] = useState<{ first: string; last: string; daily: boolean } | null>(null)

  // --- Загрузка описания ---------------------------------------------------
  useEffect(() => {
    let cancelled = false
    setDescriptor(null)
    setResult(null)
    setError(null)

    api
      .report(code)
      .then((loaded) => {
        if (cancelled) return
        setDescriptor(loaded)
        // Значения по умолчанию попадают в адрес, чтобы ссылка была полной.
        if (!params.get('g') || !params.get('m')) {
          patch({
            g: params.get('g') ?? loaded.default_group_by.join(','),
            m: params.get('m') ?? loaded.default_metrics.join(','),
          })
        }
      })
      .catch((caught) => {
        if (!cancelled) setError(caught instanceof ApiError ? caught : new ApiError('Ошибка', 500))
      })

    return () => {
      cancelled = true
    }
  }, [code])

  useEffect(() => {
    setExtent(null)
    if (!descriptor?.has_period) return
    const dateDimension = descriptor.dimensions.find((dimension) => dimension.type === 'date')
    const metric = descriptor.default_metrics[0] ?? descriptor.metrics[0]?.key
    if (!dateDimension || !metric) return
    let cancelled = false
    api
      .runReport(
        descriptor.code,
        { period: { from: monthsAgo(60), to: endOfCurrentMonth() }, group_by: [dateDimension.key], metrics: [metric] },
        asUser || null,
      )
      .then((loaded) => {
        if (cancelled) return
        const dates = loaded.rows
          .filter((row) => Number(row[metric] ?? 0) !== 0)
          .map((row) => String(row[dateDimension.key]).slice(0, 10))
          .sort()
        if (dates.length > 0) setExtent({ first: dates[0]!, last: dates[dates.length - 1]!, daily: dateDimension.key !== 'period' })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [descriptor, asUser])

  // Названия своих городов — для понятного отказа «вам доступен только …».
  useEffect(() => {
    if (!user.scope.branch?.allow) return
    api
      .dimensionValues('branch')
      .then((response) => setScopeNames(response.values.map((item) => item.name)))
      .catch(() => undefined)
  }, [user.scope.branch?.allow])

  useEffect(() => {
    if (!user.is_admin) return
    api.adminUsers().then((response) => setPeople(response.users)).catch(() => undefined)
  }, [user.is_admin])

  useEffect(() => {
    if (!descriptor) return
    for (const dimension of descriptor.dimensions) {
      if (!dimension.filterable || values[dimension.key]) continue
      api
        .dimensionValues(dimension.key)
        .then((response) => setValues((current) => ({ ...current, [dimension.key]: response.values })))
        // Список закрыт (403/404) — фильтр по измерению просто не показывается.
        .catch(() => setValues((current) => ({ ...current, [dimension.key]: [] })))
    }
  }, [descriptor, values])

  // --- Выполнение ----------------------------------------------------------
  const requestBody = useMemo(
    () => ({
      ...(descriptor?.has_period ? { period } : {}),
      group_by: groupBy,
      metrics,
      filters,
      ...(descriptor?.has_period && compare ? { compare } : {}),
    }),
    [descriptor, period, groupBy, metrics, filters, compare],
  )

  // Номер последнего запроса: ответ на устаревший (быстро переключили
  // разрез, период или отчёт) не применяется — на экране всегда цифры
  // для того, что сейчас выбрано, а не для того, что ответило последним.
  const latest = useRef(0)
  const run = useCallback(async () => {
    const id = ++latest.current
    if (!descriptor || descriptor.code !== code || groupBy.length === 0 || metrics.length === 0) {
      setBusy(false)
      return
    }
    // Перепутанные даты сначала поправляются в адресе, запрос — уже по ним.
    if (inverted || unreadable) return

    setBusy(true)
    setError(null)

    try {
      const loaded = await api.runReport(descriptor.code, requestBody, asUser || null)
      if (id === latest.current) setResult(loaded)
    } catch (caught) {
      if (id !== latest.current) return
      setError(caught instanceof ApiError ? caught : new ApiError('Сервер не отвечает — проверьте связь и повторите.', 0))
      setResult(null)
    } finally {
      if (id === latest.current) setBusy(false)
    }
  }, [descriptor, code, groupBy, metrics, requestBody, asUser, inverted, unreadable])

  useEffect(() => {
    void run()
  }, [run])

  // Новый разрез считается, на экране — прежние цифры, приглушённые.
  // С задержкой: быстрый ответ не должен мигать.
  const refreshing = useDelayedFlag(busy && result !== null)
  const phone = useMediaQuery('(max-width: 640px)')
  const [density, setDensity] = useState<'cozy' | 'compact'>(() => {
    try {
      return localStorage.getItem('svodka-density') === 'compact' ? 'compact' : 'cozy'
    } catch {
      return 'cozy'
    }
  })
  const [sheetOpen, setSheetOpen] = useState(false)
  const activeCount = Object.values(filters).filter((list) => list.length > 0).length + (custom ? 1 : 0) + (compare ? 1 : 0)

  const exportTo = async (format: 'xlsx' | 'csv') => {
    setExporting(format)
    try {
      // «Смотреть от имени» — выгрузка тем же срезом, что на экране.
      await api.exportReport(code, requestBody, format, asUser || null)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Не удалось выгрузить', 500))
    } finally {
      setExporting(null)
    }
  }

  if (error && !descriptor) {
    return (
      <Card>
        <Empty
          icon={error.isForbidden ? 'lock' : 'alert'}
          title={error.isForbidden ? 'Отчёт вам недоступен' : 'Не удалось открыть отчёт'}
          hint={error.isForbidden ? humanForbidden(code, error.message) : error.message}
        />
      </Card>
    )
  }

  if (!descriptor) return <Loading rows={5} />

  const toggle = (list: string[], key: string, param: 'g' | 'm') => {
    const next = list.includes(key) ? list.filter((item) => item !== key) : [...list, key]
    if (next.length > 0) patch({ [param]: next.join(',') })
  }

  // Деньги в приоритете; без них — процент или число (маржа, дни запаса).
  const metricType = (key: string) => descriptor.metrics.find((m) => m.key === key)?.type
  const chartMetric =
    metrics.find((key) => metricType(key) === 'money') ?? metrics.find((key) => metricType(key) === 'percent' || metricType(key) === 'number')
  const chartColumn = descriptor.metrics.find((metric) => metric.key === chartMetric)
  const primaryDimension = groupBy[0]
  const isTimeSeries = primaryDimension === 'period' || primaryDimension === 'snapshot_date'

  const { columns, rows, totals } = withComparison(result, descriptor)

  return (
    <div className="stack" style={{ gap: 18 }}>
      {/* --- Панель настройки: на телефоне — кнопка и лист снизу --- */}
      {phone && (
        <div className="report-filterbar">
          <button type="button" className="btn" onClick={() => setSheetOpen(true)} aria-haspopup="dialog">
            <Icon name="filter" /> Фильтры{activeCount > 0 ? ` · ${activeCount}` : ''}
          </button>
          <span className="small secondary">
            {descriptor.has_period ? `${formatDate(period.from)} — ${formatDate(period.to)}` : 'без периода'}
          </span>
        </div>
      )}
      {(() => {
        const controls = (
      <Card className="report-controls">
        <div className="report-controls__grid">
          {descriptor.has_period ? (
            <>
              <span className="report-controls__label">Период</span>
              <div className="report-controls__value">
                <div className="row">
                  <Segmented
                    label="Период"
                    value={preset}
                    onChange={(code) => (code === 'custom' ? patch({ from: period.from, to: period.to }) : patch({ p: code, from: null, to: null }))}
                    options={[...PERIOD_PRESETS.map((item) => ({ value: item.code, label: item.title })), { value: 'custom', label: 'Свой период' }]}
                  />
                  <Segmented
                    label="Сравнение"
                    value={compare || 'none'}
                    onChange={(code) => patch({ c: code === 'none' ? null : code })}
                    options={COMPARE_OPTIONS.map((item) => ({ value: item.code || 'none', label: item.title }))}
                  />
                </div>
                <div className="row report-period">
                  {custom ? (
                    <span className="row row--tight">
                      <label className="report-period__field">
                        <span className="sr-only">С месяца</span>
                        <input
                          type="month"
                          className="input input--sm"
                          value={period.from.slice(0, 7)}
                          max={period.to.slice(0, 7)}
                          onChange={(event) => event.target.value && patch({ from: `${event.target.value}-01` })}
                        />
                      </label>
                      <span className="muted">—</span>
                      <label className="report-period__field">
                        <span className="sr-only">По месяц</span>
                        <input
                          type="month"
                          className="input input--sm"
                          value={period.to.slice(0, 7)}
                          min={period.from.slice(0, 7)}
                          onChange={(event) => event.target.value && patch({ to: monthEndOf(event.target.value) })}
                        />
                      </label>
                    </span>
                  ) : (
                    <span className="small secondary mono">
                      {formatDate(period.from)} — {formatDate(period.to)}
                    </span>
                  )}
                  {period.to >= todayIso() && (
                    <span className="small report-period__hint">
                      Текущий месяц ещё идёт и в 1С не закрыт: данные — по сегодняшний день.
                      {compare && result?.meta.comparison_note ? ' Сравнение неполное — оговорка под таблицей.' : ''}
                    </span>
                  )}
                  {extent && lacksTail(period, extent) && (
                    <span className="small report-period__hint">
                      {period.from > extent.last ? 'В периоде нет данных' : 'Часть периода без данных'}: последние — {extentLabel(extent)}.{' '}
                      <button type="button" className="link-button" style={{ padding: 0 }} onClick={() => patch(lastYearOf(extent))}>
                        Показать 12 месяцев по {extentLabel(extent)}
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <span className="report-controls__label">Период</span>
              <div className="report-controls__value tiny muted">
                Отчёт не привязан к календарному периоду: в выгрузке есть только метка периода,
                поэтому фильтр по датам и сравнение к нему неприменимы.
              </div>
            </>
          )}

          <span className="report-controls__label">Разрез</span>
          <div className="report-controls__value chip-group" role="group" aria-label="Разрез">
            {descriptor.dimensions.map((dimension) => (
              <CheckChip key={dimension.key} checked={groupBy.includes(dimension.key)} onClick={() => toggle(groupBy, dimension.key, 'g')}>
                {dimension.title}
              </CheckChip>
            ))}
          </div>

          <span className="report-controls__label">Показатели</span>
          <div className="report-controls__value chip-group" role="group" aria-label="Показатели">
            {descriptor.metrics.map((metric) => (
              <CheckChip key={metric.key} checked={metrics.includes(metric.key)} onClick={() => toggle(metrics, metric.key, 'm')}>
                {metric.title}
              </CheckChip>
            ))}
          </div>

          {descriptor.dimensions.some((dimension) => dimension.filterable) && (
            <>
              <span className="report-controls__label">Фильтры</span>
              <div className="report-controls__value chip-group">
                {descriptor.dimensions
                  .filter((dimension) => dimension.filterable)
                  .map((dimension) => (
                    <FilterSelect
                      key={dimension.key}
                      title={dimension.title}
                      options={values[dimension.key] ?? []}
                      selected={filters[dimension.key] ?? []}
                      onChange={(next) => patch({ [`f.${dimension.key}`]: next.join(',') || null })}
                    />
                  ))}
              </div>
            </>
          )}

          {user.is_admin && people.length > 0 && (
            <>
              <span className="report-controls__label">Смотреть</span>
              <div className="report-controls__value row">
                <select
                  className="select"
                  style={{ maxWidth: 320 }}
                  value={asUser}
                  onChange={(event) => patch({ as: event.target.value || null })}
                  aria-label="Смотреть от имени"
                >
                  <option value="">от своего имени (администратор)</option>
                  {people
                    .filter((person) => !person.is_admin && !person.blocked_at)
                    .map((person) => (
                      <option key={person.id} value={person.id}>
                        от имени: {person.full_name}
                      </option>
                    ))}
                </select>
                {asUser && <Badge tone="accent">Предпросмотр прав</Badge>}
              </div>
            </>
          )}
        </div>
      </Card>
        )
        return phone ? (
          <Sheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title="Фильтры отчёта"
            footer={
              <button type="button" className="btn btn--primary" onClick={() => setSheetOpen(false)}>
                Показать {result ? pluralize(result.meta.row_count, 'строку', 'строки', 'строк') : ''}
              </button>
            }
          >
            {controls}
          </Sheet>
        ) : (
          controls
        )
      })()}

      {/* --- Сообщения --- */}
      {periodNotice && (
        <Notice tone="warn">
          {periodNotice}{' '}
          <button type="button" className="link-button" style={{ padding: 0 }} onClick={() => setPeriodNotice(null)}>
            Понятно
          </button>
        </Notice>
      )}

      {error && (
        <Notice tone={error.isForbidden ? 'warn' : 'bad'}>
          {error.isForbidden ? <strong>Доступ ограничен. </strong> : <strong>Ошибка. </strong>}
          {error.isForbidden ? humanForbidden(code, error.message) : error.message}
        </Notice>
      )}

      {result?.meta.warnings.filter((warning) => warning !== result.meta.comparison_note).map((warning) => (
        <Notice key={warning} tone="warn">
          {warning}
        </Notice>
      ))}

      {/* --- Результат --- */}
      {busy && !result ? (
        <>
          <SkeletonPanel height={200} />
          <Card title="Таблица" flush>
            <Loading rows={7} columns={Math.min(6, Math.max(3, groupBy.length + metrics.length))} />
          </Card>
        </>
      ) : result ? (
        <>
          {chartColumn && !isTimeSeries && result.rows.length > 1 && (
            <Card
              refreshing={refreshing}
              title={`${chartColumn.title} по разрезу «${descriptor.dimensions.find((d) => d.key === primaryDimension)?.title}»`}
              subtitle={
                /* Полосы — по убыванию показателя, а не в порядке выдачи сервера:
                   «первые 12 строк» и «12 крупнейших» — разные вещи. */
                <>
                  {result.rows.length > 12
                    ? `12 крупнейших из ${result.rows.length}`
                    : pluralize(result.rows.length, 'значение', 'значения', 'значений')}
                  {groupBy.length > 1 ? ` · строки разбиты ещё и по «${groupBy.slice(1).map((key) => descriptor.dimensions.find((d) => d.key === key)?.title ?? key).join('», «')}»` : ''}
                </>
              }
            >
              <BarRows
                ariaLabel={chartColumn.title}
                series={[{ key: chartColumn.key, label: chartColumn.title, color: 'var(--series-1)' }]}
                rows={[...result.rows]
                  .sort((a, b) => Math.abs(Number(b[chartColumn.key] ?? 0)) - Math.abs(Number(a[chartColumn.key] ?? 0)))
                  .slice(0, 12)
                  .map((row, index) => ({
                    key: `${groupBy.map((key) => String(row[`${key}_guid`] ?? row[key] ?? '')).join('|')}#${index}`,
                    label: groupBy.map((key) => String(row[key] ?? '—')).join(' · '),
                    values: [Number(row[chartColumn.key] ?? 0)],
                    format: (value: number) => chartFormat(chartColumn.type, value),
                  }))}
              />
            </Card>
          )}

          {chartColumn && isTimeSeries && groupBy.length === 1 && result.rows.length > 1 && (
            <Card refreshing={refreshing} title={`${chartColumn.title} по ${primaryDimension === 'period' ? 'месяцам' : 'датам'}`}>
              <ColumnChart
                ariaLabel={`${chartColumn.title} по месяцам`}
                height={200}
                format={(value) => chartFormat(chartColumn.type, value)}
                series={[{ key: chartColumn.key, label: chartColumn.title, color: 'var(--series-1)' }]}
                points={[...result.rows]
                  .sort((a, b) => String(a[primaryDimension!]).localeCompare(String(b[primaryDimension!])))
                  .slice(-24)
                  .map((row) => {
                    const date = String(row[primaryDimension!]).slice(0, 10)
                    return {
                      key: date,
                      label: primaryDimension === 'period' ? shortMonth(date) : date.slice(8, 10),
                      title: primaryDimension === 'period' ? monthLabel(date) : date,
                      values: [Number(row[chartColumn.key] ?? 0)],
                    }
                  })}
              />
              {result.rows.length > 24 && <div className="tiny muted" style={{ marginTop: 8 }}>Показаны последние 24 из {result.rows.length}.</div>}
            </Card>
          )}

          <Card
            refreshing={refreshing}
            title="Таблица"
            subtitle={
              <>
                {pluralize(result.meta.row_count, 'строка', 'строки', 'строк')}
                {result.meta.limited && ' (выдача усечена)'} · сортировка по клику на заголовок
              </>
            }
            actions={
              <>
                <Segmented
                  size="sm"
                  label="Плотность таблицы"
                  value={density}
                  onChange={(next) => {
                    setDensity(next)
                    try {
                      localStorage.setItem('svodka-density', next)
                    } catch {
                      // без хранилища — до перезагрузки
                    }
                  }}
                  options={[
                    { value: 'cozy', label: 'Просторно' },
                    { value: 'compact', label: 'Плотно' },
                  ]}
                />
                <Badge>{result.report.source}</Badge>
                {result.meta.data_as_of && (
                  <Badge tone="accent">данные на {formatDateTime(result.meta.data_as_of)}</Badge>
                )}
                <button
                  className="btn btn--sm"
                  onClick={() => exportTo('xlsx')}
                  disabled={exporting !== null || result.rows.length === 0}
                  title={result.rows.length === 0 ? 'Выгружать нечего: в таблице нет строк' : 'Выгрузка учитывает те же права и те же фильтры, что и экран'}
                >
                  {exporting === 'xlsx' ? 'Готовим…' : 'Excel'}
                </button>
                <button
                  className="btn btn--sm"
                  onClick={() => exportTo('csv')}
                  disabled={exporting !== null || result.rows.length === 0}
                  title={result.rows.length === 0 ? 'Выгружать нечего: в таблице нет строк' : undefined}
                >
                  {exporting === 'csv' ? 'Готовим…' : 'CSV'}
                </button>
              </>
            }
            flush
          >
            <div className={density === 'compact' ? 'density-compact' : undefined}>
            <DataTable
              columns={columns}
              rows={rows}
              totals={totals}
              emptyText={
                (Object.keys(result.meta.scope_applied).length > 0
                  ? 'Нет строк в границах ваших прав доступа'
                  : 'Нет строк за выбранный период') +
                (extent && descriptor.has_period ? ` · последние данные — ${extentLabel(extent)}` : '')
              }
              emptyAction={
                extent && descriptor.has_period && period.from > extent.last ? (
                  <button type="button" className="btn btn--sm" onClick={() => patch(lastYearOf(extent))}>
                    Показать 12 месяцев по {extentLabel(extent)}
                  </button>
                ) : undefined
              }
            />
            </div>
          </Card>

          {result.comparison && result.meta.comparison_note && (
            <Notice tone="warn">{result.meta.comparison_note}</Notice>
          )}

          {result.comparison && (
            <div className="tiny muted">
              Сравнение с периодом {formatDate(result.comparison.period.from)} — {formatDate(result.comparison.period.to)}.
              {Object.keys(result.comparison.rows).length === 0 &&
                ' При группировке по датам построчное сравнение не строится: значение периода по определению другое.'}
            </div>
          )}

          {Object.keys(result.meta.scope_applied).length > 0 && (
            <div className="tiny muted">
              Выдача ограничена правами доступа по:{' '}
              {Object.keys(result.meta.scope_applied)
                .map((key) => descriptor.dimensions.find((d) => d.key === key)?.title ?? key)
                .join(', ')}
              . Итоги посчитаны по выданным строкам.
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

const isIsoDate = (value: string | null): boolean => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)))

/**
 * Есть ли в периоде хвост без данных. Будущие дни текущего месяца — не хвост:
 * их ещё не было. Помесячный отчёт сравнивается по месяцам (последние данные
 * за октябрь покрывают октябрь), подневный — по дням, с запасом в один день:
 * выгрузка за сегодня приходит завтра.
 */
function lacksTail(period: Period, extent: { last: string; daily: boolean }): boolean {
  const today = todayIso()
  const end = period.to < today ? period.to : today
  if (!extent.daily) return end.slice(0, 7) > extent.last.slice(0, 7)
  const yesterday = new Date(Date.parse(`${today}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10)
  const tolerated = end === today ? yesterday : end
  return tolerated > extent.last
}

function monthEndOf(month: string): string {
  const [year, index] = month.split('-').map(Number)
  return new Date(Date.UTC(year!, index!, 0)).toISOString().slice(0, 10)
}

function extentLabel(extent: { last: string; daily: boolean }): string {
  return extent.daily ? formatDate(extent.last) : formatMonth(`${extent.last.slice(0, 7)}-01`)
}

/** Двенадцать месяцев, которыми заканчиваются данные отчёта. */
function lastYearOf(extent: { last: string }): Record<string, string | null> {
  const year = Number(extent.last.slice(0, 4))
  const month = Number(extent.last.slice(5, 7))
  const from = new Date(Date.UTC(year, month - 12, 1)).toISOString().slice(0, 10)
  return { from, to: monthEndOf(extent.last.slice(0, 7)), p: null }
}

const splitParam = (value: string | null): string[] =>
  value ? value.split(',').map((item) => item.trim()).filter(Boolean) : []

/**
 * Добавление колонок изменения к прошлому периоду.
 *
 * Дельты считаются во фронтенде из уже полученных значений: сервер отдаёт
 * обе выборки, а сопоставление строк идёт по ключу из GUID, а не по позиции.
 */
function withComparison(
  result: ReportResult | null,
  descriptor: ReportDescriptor,
): { columns: ReportColumn[]; rows: Record<string, unknown>[]; totals: Record<string, number | string | null> } {
  if (!result) return { columns: [], rows: [], totals: {} }
  if (!result.comparison) return { columns: result.columns, rows: result.rows, totals: result.totals }

  const comparison = result.comparison
  const groupBy = result.meta.group_by
  const hasRowIndex = Object.keys(comparison.rows).length > 0

  // Проценты сравниваются в процентных пунктах, всё остальное — в процентах.
  const comparable = result.columns.filter((column) =>
    ['money', 'number', 'percent'].includes(column.type),
  )
  const deltaType = (column: ReportColumn): 'delta' | 'delta_pp' =>
    column.type === 'percent' ? 'delta_pp' : 'delta'

  const columns: ReportColumn[] = []
  for (const column of result.columns) {
    columns.push(column)
    if (comparable.includes(column)) {
      columns.push({ key: `${column.key}__delta`, title: 'Δ', type: deltaType(column) })
    }
  }

  const keyOf = (row: Record<string, unknown>): string =>
    groupBy
      .map((key) => {
        const filterable = descriptor.dimensions.find((item) => item.key === key)?.filterable
        return String((filterable ? row[`${key}_guid`] : null) ?? row[key] ?? '')
      })
      .join('|')

  const rows = result.rows.map((row) => {
    const previous = hasRowIndex ? comparison.rows[keyOf(row)] : undefined
    const augmented: Record<string, unknown> = { ...row }
    for (const column of comparable) {
      const change = column.type === 'percent' ? deltaPoints : deltaPercent
      augmented[`${column.key}__delta`] = previous
        ? change(row[column.key], previous[column.key])
        : null
    }
    return augmented
  })

  const totals: Record<string, number | string | null> = { ...result.totals }
  for (const column of comparable) {
    const change = column.type === 'percent' ? deltaPoints : deltaPercent
    totals[`${column.key}__delta`] = change(result.totals[column.key], comparison.totals[column.key])
  }

  return { columns, rows, totals }
}

/**
 * Множественный выбор значений измерения.
 *
 * Список приходит уже отфильтрованным по правам: человек не должен видеть
 * в выпадающем списке филиалы, к которым у него нет доступа — это само
 * по себе раскрывает структуру компании.
 */
function FilterSelect({
  title,
  options,
  selected,
  onChange,
}: {
  title: string
  options: DimensionValue[]
  selected: string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const anchor = useRef<HTMLButtonElement>(null)

  /**
   * Список рисуется в конце страницы, а не внутри карточки.
   *
   * У карточки overflow: hidden — он нужен, чтобы таблицы не вылезали
   * за скруглённые углы, — и он же обрезал бы выпадающий список: было
   * видно верхнюю кромку панели и ничего больше. Обычные приёмы вроде
   * z-index тут не помогают: обрезка происходит раньше, чем наложение.
   */
  const place = useCallback(() => {
    const button = anchor.current
    if (!button) return

    const rect = button.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom - 16
    const width = 280

    setPosition({
      top: rect.bottom + 6,
      // Не даём уехать за правый край окна на узких экранах.
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      maxHeight: Math.max(160, Math.min(320, below)),
    })
  }, [])

  useEffect(() => {
    if (!open) return

    place()

    // Страницу прокрутили — панель осталась бы висеть в стороне от кнопки,
    // потому что она больше не внутри неё. Проще закрыть, чем догонять.
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)

    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open, place])

  if (options.length === 0) return null

  const toggle = (guid: string) =>
    onChange(selected.includes(guid) ? selected.filter((item) => item !== guid) : [...selected, guid])

  return (
    <>
      <button
        ref={anchor}
        className={`chip${selected.length > 0 ? ' chip--on' : ''}`}
        onClick={() => setOpen((value) => !value)}
      >
        {title}
        {selected.length > 0 && ` · ${selected.length}`}
        <Icon name="chevron-down" size={12} />
      </button>

      {open &&
        position &&
        createPortal(
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={() => setOpen(false)} />
            <div
              className="popover"
              style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                zIndex: 61,
                width: 280,
                maxHeight: position.maxHeight,
              }}
            >
              <div style={{ padding: 6 }}>
                {selected.length > 0 && (
                  <>
                    <button className="btn btn--ghost btn--sm" style={{ width: '100%' }} onClick={() => onChange([])}>
                      Сбросить выбор
                    </button>
                    <div className="divider" />
                  </>
                )}
                {options.map((option) => (
                  <label key={option.guid} className="popover__option">
                    <input
                      type="checkbox"
                      checked={selected.includes(option.guid)}
                      onChange={() => toggle(option.guid)}
                    />
                    <span className="small truncate">{option.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  )
}

function chartFormat(type: string, value: number): string {
  if (type === 'percent') return formatPercent(value)
  if (type === 'number') return formatNumber(Math.round(value * 100) / 100)
  return formatMoneyShort(value)
}
